import { ChangeDetectorRef, Component, ElementRef, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { Subscription } from "rxjs";
import {
  BscArea,
  BscAreaCos,
  BscAreaUsuario,
  BscCiclo,
  BscCicloTrimestre,
  BscKpi,
  BscRes,
  RolAreaBsc,
  ViaCapturaBsc,
} from "src/app/shared/model/bsc/bsc.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { IndicadoresNormaBscComponent } from "./indicadores-norma-bsc.component";
import { BscService } from "../bsc.service";
import {
  SelectorCorteBscComponent,
  SeleccionCorteBsc,
} from "../selector-corte/selector-corte-bsc.component";

/**
 * Lo editable del ciclo, en las unidades de la pantalla: umbrales y
 * objetivo en porcentaje (95 = 0.95) y la tolerancia en puntos (0.5 =
 * 0.005). La API viaja en fracciones 0..1 (docs/BSC.md §1.9).
 */
interface FormaCiclo {
  verde: number | null;
  amarillo: number | null;
  objetivo: number | null;
  diaLimite: number | null;
  anticipacion: number | null;
  excluirKpiNa: boolean;
  exigirSumaPesos: boolean;
  toleranciaPts: number | null;
  exigirMeses: boolean;
  /** bsc_ciclo.version_formato: la plantilla FOR-011 con que se exporta y se compara al importar. */
  version: string;
}

/** Las 4 fechas de bsc_ciclo_trimestre. */
type CampoFecha = "fechaCierreCaptura" | "fechaLimiteEnvio" | "fechaFinRevision" | "fechaPresentacion";

/** Un renglón de "Fechas del ciclo": las 4 fechas `yyyy-MM-dd` y el objetivo del Q en %. */
interface FormaTrimestre {
  trimestre: number;
  fechaCierreCaptura: string;
  fechaLimiteEnvio: string;
  fechaFinRevision: string;
  fechaPresentacion: string;
  objetivo: number | null;
}

/** Una persona que se puede elegir en «Persona N», con su nombre ya normalizado para el buscador. */
interface OpcionPersona {
  idUsuario: number;
  usuario: string;
  /** El nombre sin acentos y en minúsculas (normalizaBusqueda). */
  clave: string;
}

/** Un renglón «Persona N / Rol» del editor, con lo que se escribió en su buscador y sus opciones ya filtradas. */
interface FilaPersona {
  usuario: BscAreaUsuario;
  filtro: string;
  opciones: OpcionPersona[];
}

/**
 * El área abierta en el editor y la lista completa de sus usuarios.
 *
 * Las opciones de los selectores se arman una vez al abrir el editor (y al
 * recargar catálogos), y las de cada «Persona N» solo cuando cambia lo que
 * se escribe en su buscador; nunca en cada detección de cambios: un arreglo
 * nuevo con un objeto nuevo en cada pasada hacía que mat-select recreara la
 * opción, volviera a marcar la vista y la pestaña se quedara en un ciclo sin
 * fin cuando el área o el usuario ligado ya no venía en el catálogo
 * (adm_area de legado, persona dada de baja).
 */
interface EdicionArea {
  area: BscArea;
  activa: boolean;
  filas: FilaPersona[];
  opcionesAreaCos: BscAreaCos[];
  /** Los elegibles, más quienes ya estaban en el área aunque ya no lo sean. */
  personas: OpcionPersona[];
  /** Las personas ya elegidas en algún renglón: en los demás salen deshabilitadas. */
  ocupadas: Set<number>;
  /** Alta, o un área que ya no vino en la tabla: el editor va arriba de la tabla y no en su renglón. */
  arriba: boolean;
}

/**
 * Una opción de los selectores de «Fusionar KPI». Se arman una vez por
 * cambio (ver EdicionArea: mat-select no debe recibir objetos nuevos en cada
 * detección de cambios).
 */
interface OpcionKpi {
  id: number;
  texto: string;
  /** El objetivo completo (title de la opción). */
  titulo: string;
  /** El destino que está con el origen en la planeación de un mismo año (el servidor lo rechaza). */
  deshabilitado: boolean;
}

/** Largo del objetivo en los mensajes de «Fusionar KPI». */
const LARGO_OBJETIVO = 90;
/**
 * Largo del objetivo en las opciones de los selectores: una opción de
 * mat-select es de un renglón y una más larga se encima con la siguiente.
 * El objetivo completo está en la tabla y en el título de la opción.
 */
const LARGO_OPCION = 42;

/** Tope de bsc_ciclo.version_formato (BscParametroBuss.LARGO_VERSION). */
const LARGO_VERSION = 20;

/** Las 4 fechas de cada trimestre, en el orden en que deben ocurrir (§8). */
const FECHAS_TRIMESTRE: { campo: CampoFecha; texto: string }[] = [
  { campo: "fechaCierreCaptura", texto: "Cierre de captura" },
  { campo: "fechaLimiteEnvio", texto: "Límite de envío" },
  { campo: "fechaFinRevision", texto: "Fin de revisión" },
  { campo: "fechaPresentacion", texto: "Presentación" },
];

const VIAS: { valor: ViaCapturaBsc; texto: string }[] = [
  { valor: "AMBAS", texto: "Ambas" },
  { valor: "EXCEL", texto: "Excel FOR-011" },
  { valor: "GUIADO", texto: "Flujo guiado" },
];

/** Tope de BscParametroBuss.ANTICIPACION_MAX. */
const ANTICIPACION_MAX = 60;

const ROLES: { valor: RolAreaBsc; texto: string }[] = [
  { valor: "RESPONSABLE", texto: "Responsable" },
  { valor: "CAPTURISTA", texto: "Capturista" },
];

/**
 * BSC · T4 Parámetros y reglas del año (docs/BSC.md §7 y §8).
 *
 * Calco de asistencia/parametros: bloques table-page__card por tema, cada
 * uno con su explicación, porque son decisiones de negocio (qué es "en
 * meta", cuándo vence cada trimestre) y no ajustes técnicos.
 *
 * Por año con herencia: si el año elegido aún no tiene ciclo, el servidor
 * contesta el anterior más reciente con las fechas pasadas al año pedido
 * (o los valores por omisión) marcado `heredado`, y se avisa que nada está
 * guardado hasta pulsar Guardar.
 *
 * Desde el primer trimestre AUTORIZADO del ciclo, semáforo, objetivo y
 * regla NA quedan deshabilitados (el servidor igual los rechaza con
 * VAL-BSC-BLOQUEADO); un ciclo CERRADO es todo de solo lectura. Las áreas
 * no son del año: se editan siempre.
 *
 * F7.2: en un año importado del FOR-011 (IMPORTACION_*) y abierto,
 * «Recalcular snapshots» vuelve a congelar con los parámetros guardados el
 * alcance y el semáforo de sus trimestres autorizados; y «Fusionar KPI», por
 * área y sin año, junta dos KPI maestros que son el mismo con otra redacción
 * (el comparativo los empareja desde ahí).
 */
@Component({
  selector: "app-parametros-bsc",
  standalone: true,
  templateUrl: "./parametros-bsc.component.html",
  // dashboard-tarjetas.css para el bloque `meta` que se proyecta en el
  // selector: lleva el _ngcontent de esta pantalla, no el del selector
  // (mismo caso que dashboard-asistencia).
  styleUrls: ["../../dashboard/shared/dashboard-tarjetas.css", "./parametros-bsc.component.css"],
  imports: [
    CommonModule,
    IndicadoresNormaBscComponent,
    FormsModule,
    RouterLink,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    NgxMatSelectSearchModule,
    SelectorCorteBscComponent,
  ],
})
export class ParametrosBscComponent {
  @ViewChild(SelectorCorteBscComponent) selector?: SelectorCorteBscComponent;

  readonly fechasTrimestre = FECHAS_TRIMESTRE;
  readonly vias = VIAS;
  readonly roles = ROLES;
  readonly anticipacionMax = ANTICIPACION_MAX;
  readonly largoVersion = LARGO_VERSION;
  /** Personas > Empleados: donde se reparten los perfiles (D10). */
  readonly rutaPerfiles = "/EMPLEADO/GESTION_EMPLEADOS";

  anio: number | null = null;
  guardandoIndicadores = false;
  loading = false;
  guardando = false;
  guardandoArea = false;
  cerrando = false;
  /** Lo que impidió consultar (sin permiso, sin sesión, sin conexión). */
  error = "";

  ciclo: BscCiclo | null = null;
  forma: FormaCiclo = this.formaVacia();
  trimestres: FormaTrimestre[] = [];
  areas: BscArea[] = [];
  usuariosElegibles: BscAreaUsuario[] = [];
  /** usuariosElegibles como opciones del buscador; se arman al llegar la consulta. */
  private personasElegibles: OpcionPersona[] = [];
  areasCos: BscAreaCos[] = [];
  unidades: string[] = [];
  bloqueos: string[] = [];
  perfilesConLlave: string[] = [];
  umbralesBloqueados = false;

  edicion: EdicionArea | null = null;
  /** El botón que abrió el editor (editar del área o «Agregar área»): ahí regresa el foco al cerrarlo. */
  private focoEditor: string | null = null;
  /** El foco que se repone cuando la tabla vuelve a pintarse tras guardar un área. */
  private focoPendiente: string | null = null;
  /** El aviso de «área guardada» sigue abierto: el foco espera a que se cierre (ver reponeFoco). */
  private avisoAbierto = false;

  /* ---------- Recalcular snapshots (F7.2) ---------- */
  /** Trimestres autorizados de un año importado y abierto; nulo = no se ofrece (PLANEACION, cerrado, heredado). */
  snapshotsRecalculables: number | null = null;
  recalculando = false;
  /** Lo que dijo el último recálculo del año (cada cambio en palabras). */
  resultadoRecalculo: string[] = [];

  /* ---------- KPI maestro y Fusionar KPI (F7.2) ---------- */
  kpiArea: number | null = null;
  kpisMaestro: BscKpi[] = [];
  cargandoKpis = false;
  errorKpis = "";
  fusion: { origen: number | null; destino: number | null } = { origen: null, destino: null };
  opcionesOrigen: OpcionKpi[] = [];
  opcionesDestino: OpcionKpi[] = [];
  fusionando = false;

  /** La consulta en vuelo: se cancela si el año cambia antes de que conteste. */
  private consulta?: Subscription;
  /** La consulta de KPI en vuelo: se cancela si cambia el área. */
  private consultaKpis?: Subscription;
  /** Forma y fechas como llegaron del servidor, para saber si hay cambios sin guardar. */
  private huella = "";

  constructor(
    private readonly bscService: BscService,
    private readonly msg: Msg,
    private readonly cdr: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
  ) {}

  /* ============================ Consulta ============================ */

  onSeleccion(seleccion: SeleccionCorteBsc): void {
    const previo = this.anio;
    if (previo == null || previo === seleccion.anio || !this.hayCambios) {
      this.cambiaAnio(seleccion.anio);
      return;
    }
    this.msg.questionCall(
      `Hay cambios sin guardar en ${previo}. Si cambias a ${seleccion.anio} se pierden.`,
      null,
      (resultado: { isConfirmed?: boolean }) => {
        if (resultado?.isConfirmed) {
          this.cambiaAnio(seleccion.anio);
        } else {
          this.selector?.fijarAnio(previo);
        }
      },
    );
  }

  private cambiaAnio(anio: number): void {
    this.anio = anio;
    this.edicion = null;
    this.focoEditor = null;
    this.focoPendiente = null;
    this.resultadoRecalculo = [];
    this.consultar();
  }

  /** Parámetros o fechas editados, o un área abierta en el editor. */
  get hayCambios(): boolean {
    return !!this.edicion || (!!this.ciclo && !this.cerrado && this.huellaActual() !== this.huella);
  }

  /** Parámetros o fechas del año editados y sin guardar (sin contar el editor de áreas). */
  get anioSinGuardar(): boolean {
    return !!this.ciclo && !this.cerrado && this.huellaActual() !== this.huella;
  }

  private huellaActual(): string {
    return JSON.stringify({ forma: this.forma, trimestres: this.trimestres });
  }

  /**
   * @param conservarForma vuelve a leer catálogos y áreas sin tirar lo que
   *        se editó del año y aún no se guarda (al guardar un área).
   */
  consultar(conservarForma = false): void {
    if (this.anio == null) {
      return;
    }
    // Solo cuenta la respuesta del año que sigue elegido: si el usuario
    // cambia rápido, una respuesta vieja no pinta otro año.
    const pedido = this.anio;
    this.consulta?.unsubscribe();
    this.loading = true;
    this.error = "";
    this.cdr.detectChanges();

    this.consulta = this.bscService.parametrosConsultar(pedido).subscribe({
      next: (res) => {
        if (pedido !== this.anio) {
          return;
        }
        this.loading = false;
        if (res?.codigoError) {
          this.error = this.mensaje(res, "No se pudieron consultar los parámetros del BSC.");
          this.ciclo = null;
          this.focoPendiente = null;
          this.cdr.detectChanges();
          return;
        }
        this.aplica(res, conservarForma);
        this.cdr.detectChanges();
        this.reponeFoco();
      },
      error: () => {
        if (pedido !== this.anio) {
          return;
        }
        this.loading = false;
        this.error = "No se pudieron consultar los parámetros del BSC: sin conexión con el servidor.";
        this.ciclo = null;
        this.focoPendiente = null;
        this.cdr.detectChanges();
      },
    });
  }

  private aplica(res: BscRes, conservarForma = false): void {
    const pendiente = conservarForma ? { forma: this.forma, trimestres: this.trimestres } : null;
    const ciclo: BscCiclo = res.ciclo ?? { anio: this.anio ?? undefined, heredado: true };
    this.ciclo = ciclo;
    this.forma = {
      verde: this.aPct(ciclo.semaforoVerde),
      amarillo: this.aPct(ciclo.semaforoAmarillo),
      objetivo: this.aPct(ciclo.objetivoSolser),
      diaLimite: ciclo.diaLimiteCapturaMensual ?? null,
      anticipacion: ciclo.anticipacionRecordatorioDias ?? null,
      excluirKpiNa: ciclo.excluirKpiNa !== false,
      exigirSumaPesos: ciclo.exigirSumaPesos !== false,
      toleranciaPts: this.aPct(ciclo.toleranciaPesos),
      exigirMeses: ciclo.exigirMesesParaCerrar !== false,
      version: ciclo.versionFormato ?? "",
    };
    const porQ = new Map<number, BscCicloTrimestre>();
    (res.trimestresCiclo ?? []).forEach((t) => {
      if (t.trimestre != null) {
        porQ.set(t.trimestre, t);
      }
    });
    this.trimestres = [1, 2, 3, 4].map((q) => {
      const t = porQ.get(q) ?? {};
      return {
        trimestre: q,
        fechaCierreCaptura: t.fechaCierreCaptura ?? "",
        fechaLimiteEnvio: t.fechaLimiteEnvio ?? "",
        fechaFinRevision: t.fechaFinRevision ?? "",
        fechaPresentacion: t.fechaPresentacion ?? "",
        objetivo: this.aPct(t.objetivoSolser),
      };
    });
    this.areas = [...(res.areas ?? [])].sort(
      (a, b) => (a.orden ?? 0) - (b.orden ?? 0) || (a.nombre ?? "").localeCompare(b.nombre ?? ""),
    );
    this.usuariosElegibles = [...(res.usuarios ?? [])].sort((a, b) =>
      (a.usuario ?? "").localeCompare(b.usuario ?? ""),
    );
    this.personasElegibles = aOpcionesPersona(this.usuariosElegibles);
    this.areasCos = [...(res.areasCos ?? [])].sort((a, b) => (a.area ?? "").localeCompare(b.area ?? ""));
    this.unidades = res.unidades ?? [];
    this.bloqueos = res.bloqueos ?? [];
    this.perfilesConLlave = res.perfilesConLlave ?? [];
    // Sin la bandera explícita, cualquier bloqueo informado cuenta.
    this.umbralesBloqueados = res.umbralesBloqueados ?? this.bloqueos.length > 0;
    this.snapshotsRecalculables = res.snapshotsRecalculables ?? null;
    // La huella es la del servidor; lo pendiente se repone encima y sigue
    // contando como cambio sin guardar.
    this.huella = this.huellaActual();
    if (pendiente) {
      this.forma = pendiente.forma;
      this.trimestres = pendiente.trimestres;
    }
    if (this.edicion) {
      this.armaOpciones(this.edicion);
    }
    this.asegurarAreaKpis();
  }

  /* ============================ Estado ============================ */

  get cerrado(): boolean {
    return this.ciclo?.estatus === "CERRADO";
  }

  get heredado(): boolean {
    return this.ciclo?.heredado === true;
  }

  /** Semáforo, objetivo (anual y por Q) y regla NA. */
  get umbralesDeshabilitados(): boolean {
    return this.cerrado || this.umbralesBloqueados;
  }

  get puedeCerrarAnio(): boolean {
    return !!this.ciclo && !this.heredado && !this.cerrado;
  }

  get textoHerencia(): string {
    const origen = this.ciclo?.anioHeredado;
    // El servidor mueve las fechas los años que haya entre los dos ciclos.
    return origen
      ? `Aún no guardado: valores copiados de ${origen}; las fechas se pasaron a ${this.anio}.`
      : "Aún no guardado: valores por omisión.";
  }

  get textoOrigen(): string {
    switch (this.ciclo?.origen) {
      case "IMPORTACION_ARRANQUE":
        return "Arranque importado del FOR-011";
      case "IMPORTACION_HISTORICA":
        return "Histórico importado del FOR-011";
      default:
        return "Planeación";
    }
  }

  get textoEstatus(): string {
    if (!this.ciclo || this.heredado) {
      return "Sin guardar";
    }
    return this.cerrado ? "Cerrado" : "Abierto";
  }

  get claseEstatus(): string {
    if (!this.ciclo || this.heredado) {
      return "table-page__status-badge is-pending";
    }
    return this.cerrado ? "table-page__status-badge is-inactive" : "table-page__status-badge is-active";
  }

  /* ============================ Guardar ciclo ============================ */

  guardar(): void {
    if (!this.ciclo || this.anio == null || this.cerrado || this.guardando || this.loading) {
      return;
    }
    // La forma tiene que ser la del año elegido (ver consultar).
    if (this.ciclo.anio != null && this.ciclo.anio !== this.anio) {
      return;
    }
    const errores = this.validaCiclo();
    if (errores.length > 0) {
      this.msg.warningCall(errores.join(" "), "Revisa los parámetros", null);
      return;
    }
    const ciclo: BscCiclo = {
      ...this.ciclo,
      anio: this.anio,
      semaforoVerde: this.aFraccion(this.forma.verde),
      semaforoAmarillo: this.aFraccion(this.forma.amarillo),
      objetivoSolser: this.aFraccion(this.forma.objetivo),
      diaLimiteCapturaMensual: this.forma.diaLimite ?? undefined,
      anticipacionRecordatorioDias: this.forma.anticipacion ?? undefined,
      excluirKpiNa: this.forma.excluirKpiNa,
      exigirSumaPesos: this.forma.exigirSumaPesos,
      toleranciaPesos: this.aFraccion(this.forma.toleranciaPts),
      exigirMesesParaCerrar: this.forma.exigirMeses,
      // Vacía = la que se mostraba: la guardada, la heredada o la de
      // omisión. Sin esto, un año heredado guardaba la de omisión del
      // servidor en vez de la del año copiado.
      versionFormato: this.forma.version.trim() || this.ciclo.versionFormato || undefined,
    };
    // Lo que no se persiste no viaja.
    delete ciclo.heredado;
    delete ciclo.anioHeredado;
    const trimestres: BscCicloTrimestre[] = this.trimestres.map((t) => ({
      idCiclo: this.heredado ? undefined : this.ciclo?.idCiclo,
      trimestre: t.trimestre,
      fechaCierreCaptura: t.fechaCierreCaptura || null,
      fechaLimiteEnvio: t.fechaLimiteEnvio || null,
      fechaFinRevision: t.fechaFinRevision || null,
      fechaPresentacion: t.fechaPresentacion || null,
      objetivoSolser: this.aFraccion(t.objetivo) ?? null,
    }));
    if (this.heredado) {
      // Es la copia en memoria de otro año: el ciclo se crea al guardar.
      delete ciclo.idCiclo;
      delete ciclo.estatus;
      delete ciclo.origen;
    }

    this.guardando = true;
    this.cdr.detectChanges();
    this.bscService.parametrosGuardar(ciclo, trimestres).subscribe({
      next: (res) => {
        this.guardando = false;
        if (res?.codigoError) {
          this.msg.warningCall(this.mensaje(res, "No se pudieron guardar los parámetros."), null, null);
          this.cdr.detectChanges();
          return;
        }
        this.avisaGuardado(res, `Los parámetros de ${this.anio} quedaron guardados.`);
        this.selector?.recargar();
        this.consultar();
      },
      error: () => {
        this.guardando = false;
        this.msg.errorCall("No se pudieron guardar los parámetros: sin conexión con el servidor.", null, null);
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Las mismas reglas de §8 que valida el servidor, para no hacer el viaje
   * con algo que va a regresar.
   */
  private validaCiclo(): string[] {
    const errores: string[] = [];
    const f = this.forma;
    const enRango = (v: number | null) => v != null && v > 0 && v <= 100;
    if (!enRango(f.verde) || !enRango(f.amarillo) || !enRango(f.objetivo)) {
      errores.push("En meta, en riesgo y el objetivo Solser deben estar entre 0 y 100 %.");
    } else if ((f.verde as number) <= (f.amarillo as number)) {
      errores.push("«En meta» debe ser mayor que «en riesgo».");
    }
    if (f.diaLimite == null || f.diaLimite < 1 || f.diaLimite > 28 || !Number.isInteger(Number(f.diaLimite))) {
      errores.push("El día límite de captura va del 1 al 28.");
    }
    if (
      f.anticipacion == null ||
      f.anticipacion < 0 ||
      f.anticipacion > ANTICIPACION_MAX ||
      !Number.isInteger(Number(f.anticipacion))
    ) {
      errores.push(`La anticipación de los recordatorios va de 0 a ${ANTICIPACION_MAX} días.`);
    }
    if (f.toleranciaPts == null || f.toleranciaPts < 0 || f.toleranciaPts >= 100) {
      errores.push("La tolerancia de la suma de pesos va de 0 a menos de 100 puntos.");
    }
    if (f.version.trim().length > LARGO_VERSION) {
      errores.push(`La versión del formato admite hasta ${LARGO_VERSION} caracteres.`);
    }
    // Dentro de cada Q, en el orden de FECHAS_TRIMESTRE; entre trimestres,
    // cada fecha después de la misma del Q anterior. Las vacías no cuentan.
    const ultimaPorCampo = new Map<string, { valor: string; q: number }>();
    for (const t of this.trimestres) {
      if (t.objetivo != null && (t.objetivo <= 0 || t.objetivo > 100)) {
        errores.push(`El objetivo de Q${t.trimestre} debe estar entre 0 y 100 %.`);
      }
      this.validaFechas(t, ultimaPorCampo, errores);
    }
    return errores;
  }

  /**
   * Las fechas de un trimestre (validaCiclo): en el orden de FECHAS_TRIMESTRE
   * y cada una después de la misma del Q anterior (`ultimaPorCampo`, que se
   * va llenando trimestre por trimestre). Agrega a `errores` lo que no cuadra.
   */
  private validaFechas(
    t: FormaTrimestre,
    ultimaPorCampo: Map<string, { valor: string; q: number }>,
    errores: string[],
  ): void {
    let previa: { valor: string; texto: string } | null = null;
    for (const fecha of FECHAS_TRIMESTRE) {
      const valor = t[fecha.campo];
      if (!valor) {
        continue;
      }
      const texto = fecha.texto.toLowerCase();
      if (previa && valor < previa.valor) {
        errores.push(`En Q${t.trimestre}, ${texto} no puede ser antes de ${previa.texto}.`);
      }
      const mismaAnterior = ultimaPorCampo.get(fecha.campo);
      if (mismaAnterior && valor <= mismaAnterior.valor) {
        errores.push(`${fecha.texto} de Q${t.trimestre} debe ser después de la de Q${mismaAnterior.q}.`);
      }
      previa = { valor, texto };
      ultimaPorCampo.set(fecha.campo, { valor, q: t.trimestre });
    }
  }

  /* ============================ Cerrar año ============================ */

  cerrarAnio(): void {
    if (!this.puedeCerrarAnio || this.anio == null || this.cerrando) {
      return;
    }
    const anio = this.anio;
    this.msg.questionCall(
      `Cerrar ${anio} deja el año de solo lectura: ya no se capturan avances, no se importa y no se cambian sus parámetros. ` +
        "Solo procede si todas las áreas tienen el Q4 autorizado.",
      null,
      (resultado: { isConfirmed?: boolean }) => {
        if (resultado?.isConfirmed) {
          this.confirmaCierre(anio);
        }
      },
    );
  }

  private confirmaCierre(anio: number): void {
    this.cerrando = true;
    this.cdr.detectChanges();
    this.bscService.cicloCerrar(anio).subscribe({
      next: (res) => {
        this.cerrando = false;
        if (res?.codigoError) {
          this.msg.warningCall(this.mensaje(res, `No se pudo cerrar ${anio}.`), null, null);
          this.cdr.detectChanges();
          return;
        }
        this.msg.successCall(`${anio} quedó cerrado.`, null, null);
        this.selector?.recargar();
        this.consultar();
      },
      error: () => {
        this.cerrando = false;
        this.msg.errorCall(`No se pudo cerrar ${anio}: sin conexión con el servidor.`, null, null);
        this.cdr.detectChanges();
      },
    });
  }

  /* ============================ Recalcular snapshots (F7.2) ============================ */

  /** Año importado del FOR-011, guardado y abierto (el servidor dice cuántos trimestres). */
  get puedeRecalcular(): boolean {
    return this.snapshotsRecalculables != null && !!this.ciclo && !this.heredado && !this.cerrado;
  }

  get textoRecalculables(): string {
    const n = this.snapshotsRecalculables ?? 0;
    if (n === 0) {
      return "Todavía no tiene trimestres autorizados: no hay snapshots que recalcular.";
    }
    return n === 1
      ? "Su trimestre autorizado guarda el alcance y el semáforo con que se cargó."
      : `Sus ${n} trimestres autorizados guardan el alcance y el semáforo con que se cargaron.`;
  }

  recalcular(): void {
    if (!this.puedeRecalcular || this.anio == null || this.recalculando || !this.ciclo) {
      return;
    }
    if (this.anioSinGuardar) {
      this.msg.warningCall(
        "Hay cambios sin guardar en los parámetros: el recálculo usa los guardados. Pulsa Guardar y vuelve a recalcular.",
        "Guarda primero",
        null,
      );
      return;
    }
    const anio = this.anio;
    const c = this.ciclo;
    const na = c.excluirKpiNa !== false ? "los KPI «NA» fuera del promedio hasta que aplican" : "los KPI «NA» dentro del promedio";
    this.msg.questionCall(
      `Se recalcularán los trimestres autorizados de ${anio} con los parámetros guardados: en meta desde ` +
        `${this.aPct(c.semaforoVerde) ?? "—"} %, en riesgo desde ${this.aPct(c.semaforoAmarillo) ?? "—"} % y ${na}. ` +
        "Cambia el alcance y el semáforo que muestran el tablero, el detalle del área, el comparativo y el FOR-011 " +
        "exportado; cada cambio queda en la bitácora del área. Los trimestres que autorizó Dirección en el flujo no se tocan.",
      null,
      (resultado: { isConfirmed?: boolean }) => {
        if (resultado?.isConfirmed) {
          this.confirmaRecalculo(anio);
        }
      },
    );
  }

  private confirmaRecalculo(anio: number): void {
    this.recalculando = true;
    this.resultadoRecalculo = [];
    this.cdr.detectChanges();
    this.bscService.parametrosRecalcular(anio).subscribe({
      next: (res) => {
        this.recalculando = false;
        if (res?.codigoError) {
          this.msg.warningCall(this.mensaje(res, `No se pudieron recalcular los snapshots de ${anio}.`), null, null);
          this.cdr.detectChanges();
          return;
        }
        if (anio === this.anio) {
          this.resultadoRecalculo = res.avisos ?? [];
        }
        const revisados = res.conteos?.REVISADOS ?? 0;
        const cambiados = res.conteos?.CAMBIADOS ?? 0;
        this.msg.successCall(textoRecalculo(anio, revisados, cambiados), null, null);
        this.cdr.detectChanges();
      },
      error: () => {
        this.recalculando = false;
        this.msg.errorCall(`No se pudieron recalcular los snapshots de ${anio}: sin conexión con el servidor.`, null, null);
        this.cdr.detectChanges();
      },
    });
  }

  /* ============================ KPI maestro y Fusionar KPI (F7.2) ============================ */

  /** Las áreas que se pueden elegir (con id), las activas primero. */
  get areasConId(): BscArea[] {
    return this.areas.filter((a) => a.idBscArea != null);
  }

  /** Al llegar las áreas: si el área elegida ya no está, la primera activa (y se consultan sus KPI). */
  private asegurarAreaKpis(): void {
    if (this.kpiArea != null && this.areas.some((a) => a.idBscArea === this.kpiArea)) {
      return;
    }
    const primera =
      this.areas.find((a) => a.idBscArea != null && a.estatus !== "INACTIVO") ?? this.areas.find((a) => a.idBscArea != null);
    this.kpiArea = primera?.idBscArea ?? null;
    this.consultarKpis();
  }

  onAreaKpis(idBscArea: number | null): void {
    if (idBscArea === this.kpiArea) {
      return;
    }
    this.kpiArea = idBscArea;
    this.consultarKpis();
  }

  consultarKpis(): void {
    const area = this.kpiArea;
    this.consultaKpis?.unsubscribe();
    this.fusion = { origen: null, destino: null };
    this.errorKpis = "";
    if (area == null) {
      this.kpisMaestro = [];
      this.cargandoKpis = false;
      this.armaOpcionesFusion();
      return;
    }
    this.cargandoKpis = true;
    this.consultaKpis = this.bscService.parametrosKpis(area).subscribe({
      next: (res) => {
        if (area !== this.kpiArea) {
          return;
        }
        this.cargandoKpis = false;
        if (res?.codigoError) {
          this.errorKpis = this.mensaje(res, "No se pudieron consultar los KPI del área.");
          this.kpisMaestro = [];
        } else {
          this.kpisMaestro = this.ordenaKpis(res.kpisMaestro ?? []);
        }
        this.armaOpcionesFusion();
        this.cdr.detectChanges();
      },
      error: () => {
        if (area !== this.kpiArea) {
          return;
        }
        this.cargandoKpis = false;
        this.errorKpis = "No se pudieron consultar los KPI del área: sin conexión con el servidor.";
        this.kpisMaestro = [];
        this.armaOpcionesFusion();
        this.cdr.detectChanges();
      },
    });
  }

  /** Los activos primero, del que aparece en el año más reciente al más viejo; los retirados al final. */
  private ordenaKpis(kpis: BscKpi[]): BscKpi[] {
    const ultimo = (k: BscKpi) => Math.max(0, ...(k.anios ?? []));
    return [...kpis].sort(
      (a, b) =>
        Number(a.estatus === "RETIRADO") - Number(b.estatus === "RETIRADO") ||
        ultimo(b) - ultimo(a) ||
        (a.objetivo ?? "").localeCompare(b.objetivo ?? ""),
    );
  }

  onOrigen(id: number | null): void {
    this.fusion = { origen: id, destino: this.fusion.destino === id ? null : this.fusion.destino };
    this.armaOpcionesFusion();
    // Un destino que quedó con un año en común ya no vale.
    if (this.fusion.destino != null && this.opcionesDestino.find((o) => o.id === this.fusion.destino)?.deshabilitado) {
      this.fusion = { ...this.fusion, destino: null };
    }
  }

  onDestino(id: number | null): void {
    this.fusion = { ...this.fusion, destino: id };
  }

  private armaOpcionesFusion(): void {
    const activos = this.kpisMaestro.filter((k) => k.estatus !== "RETIRADO" && k.idKpi != null);
    this.opcionesOrigen = activos.map((k) => ({
      id: k.idKpi as number,
      texto: this.textoOpcion(k),
      titulo: k.objetivo ?? "",
      deshabilitado: false,
    }));
    const origen = this.kpiPorId(this.fusion.origen);
    this.opcionesDestino = activos
      .filter((k) => k.idKpi !== this.fusion.origen)
      .map((k) => {
        const comunes = origen ? this.aniosComunes(origen, k) : [];
        return {
          id: k.idKpi as number,
          texto: this.textoOpcion(k) + (comunes.length > 0 ? " · ya en ese año" : ""),
          titulo: k.objetivo ?? "",
          deshabilitado: comunes.length > 0,
        };
      });
  }

  private textoOpcion(k: BscKpi): string {
    const anios = k.anios ?? [];
    return `${this.corto(k.objetivo, LARGO_OPCION)} · ${anios.length > 0 ? anios.join(", ") : "sin planeación"}`;
  }

  private kpiPorId(id: number | null): BscKpi | undefined {
    return id == null ? undefined : this.kpisMaestro.find((k) => k.idKpi === id);
  }

  private aniosComunes(a: BscKpi, b: BscKpi): number[] {
    const deB = new Set(b.anios ?? []);
    return (a.anios ?? []).filter((anio) => deB.has(anio));
  }

  get puedeFusionar(): boolean {
    const origen = this.kpiPorId(this.fusion.origen);
    const destino = this.kpiPorId(this.fusion.destino);
    return !!origen && !!destino && origen !== destino && this.aniosComunes(origen, destino).length === 0;
  }

  /** Lo que va a pasar, en palabras, con la pareja elegida. */
  get textoFusion(): string {
    const origen = this.kpiPorId(this.fusion.origen);
    const destino = this.kpiPorId(this.fusion.destino);
    if (!origen || !destino) {
      return "";
    }
    const deOrigen = origen.anios ?? [];
    const comunes = this.aniosComunes(origen, destino);
    if (comunes.length > 0) {
      return `No se pueden fusionar: los dos están en la planeación de ${comunes.join(", ")}. En un mismo año son dos KPI distintos.`;
    }
    const mueve =
      deOrigen.length > 0
        ? `su planeación de ${deOrigen.join(", ")} pasa a «${this.corto(destino.objetivo)}», que deja su redacción en todos los años`
        : `no está en ninguna planeación; «${this.corto(destino.objetivo)}» no cambia`;
    return (
      `«${this.corto(origen.objetivo)}» queda retirado y ${mueve}. El comparativo los toma como el mismo KPI; ` +
      "metas, pesos, avances y snapshots no cambian."
    );
  }

  fusionar(): void {
    if (!this.puedeFusionar || this.fusionando || this.fusion.origen == null || this.fusion.destino == null) {
      return;
    }
    const origen = this.fusion.origen;
    const destino = this.fusion.destino;
    const area = this.kpiArea;
    this.msg.questionCall(`${this.textoFusion} No se deshace desde esta pantalla.`, null, (resultado: { isConfirmed?: boolean }) => {
      if (resultado?.isConfirmed) {
        this.confirmaFusion(origen, destino, area);
      }
    });
  }

  private confirmaFusion(origen: number, destino: number, area: number | null): void {
    this.fusionando = true;
    this.cdr.detectChanges();
    this.bscService.parametrosFusionarKpi(origen, destino).subscribe({
      next: (res) => {
        this.fusionando = false;
        if (res?.codigoError) {
          this.msg.warningCall(this.mensaje(res, "No se pudieron fusionar los KPI."), null, null);
          this.cdr.detectChanges();
          return;
        }
        if (area === this.kpiArea) {
          this.kpisMaestro = this.ordenaKpis(res.kpisMaestro ?? []);
          this.fusion = { origen: null, destino: null };
          this.armaOpcionesFusion();
        }
        this.msg.successCall((res.avisos ?? []).join(" ") || "Los KPI quedaron fusionados.", null, null);
        this.cdr.detectChanges();
      },
      error: () => {
        this.fusionando = false;
        this.msg.errorCall("No se pudieron fusionar los KPI: sin conexión con el servidor.", null, null);
        this.cdr.detectChanges();
      },
    });
  }

  trackKpi(_: number, k: BscKpi): number | string {
    return k.idKpi ?? k.objetivo ?? "";
  }

  /** El objetivo recortado para selectores y mensajes. */
  private corto(texto: string | undefined, largo = LARGO_OBJETIVO): string {
    const t = (texto ?? "").replace(/\s+/g, " ").trim();
    return t.length <= largo ? t : `${t.slice(0, largo - 1).trim()}…`;
  }

  /* ============================ Áreas ============================ */

  nuevaArea(): void {
    const orden = this.areas.reduce((max, a) => Math.max(max, a.orden ?? 0), 0) + 1;
    this.abreEditor(
      {
        area: { nombre: "", aliasExcel: "", idArea: null, viaCaptura: "AMBAS", orden, estatus: "ACTIVO" },
        activa: true,
        filas: [],
        opcionesAreaCos: [],
        personas: [],
        ocupadas: new Set(),
        arriba: true,
      },
      ID_AGREGAR_AREA,
    );
  }

  editarArea(area: BscArea): void {
    this.abreEditor(
      {
        area: { ...area },
        activa: area.estatus !== "INACTIVO",
        filas: (area.usuarios ?? []).map((u) => ({ usuario: { ...u }, filtro: "", opciones: [] })),
        opcionesAreaCos: [],
        personas: [],
        ocupadas: new Set(),
        arriba: false,
      },
      area.idBscArea == null ? ID_AGREGAR_AREA : idEditarArea(area.idBscArea),
    );
  }

  /**
   * Pinta el editor donde toca (arriba o debajo del renglón del área), lo
   * trae a la vista y pone el foco en el nombre.
   */
  private abreEditor(e: EdicionArea, focoAlCerrar: string): void {
    this.edicion = this.armaOpciones(e);
    this.focoEditor = focoAlCerrar;
    this.cdr.detectChanges();
    const raiz = this.host.nativeElement;
    raiz.querySelector<HTMLElement>(`#${ID_EDITOR}`)?.scrollIntoView({ block: "nearest" });
    raiz.querySelector<HTMLInputElement>(`#${ID_NOMBRE}`)?.focus();
  }

  cancelarArea(): void {
    const foco = this.focoEditor;
    this.edicion = null;
    this.focoEditor = null;
    this.cdr.detectChanges();
    this.enfoca(foco);
  }

  agregarUsuario(): void {
    const e = this.edicion;
    if (!e) {
      return;
    }
    const rol: RolAreaBsc = e.filas.some((f) => f.usuario.rol === "RESPONSABLE") ? "CAPTURISTA" : "RESPONSABLE";
    e.filas = [...e.filas, { usuario: { idUsuario: undefined, rol, vigente: true }, filtro: "", opciones: e.personas }];
    this.cdr.detectChanges();
    this.enfoca(idPersona(e.filas.length - 1));
  }

  quitarUsuario(indice: number): void {
    const e = this.edicion;
    if (!e) {
      return;
    }
    e.filas = e.filas.filter((_, i) => i !== indice);
    this.marcaOcupadas(e);
    // El botón con el foco desaparece: pasa al «Quitar» que quedó en su lugar, al anterior o a «Agregar usuario».
    this.cdr.detectChanges();
    const queda = Math.min(indice, e.filas.length - 1);
    this.enfoca(queda >= 0 ? idQuitar(queda) : ID_AGREGAR_USUARIO);
  }

  /** Persona elegida en un renglón: las demás filas la ven deshabilitada. */
  eligePersona(e: EdicionArea, fila: FilaPersona, idUsuario: number | undefined): void {
    fila.usuario.idUsuario = idUsuario;
    this.marcaOcupadas(e);
  }

  /**
   * Lo escrito en el buscador de un renglón (también llega "" cuando el
   * buscador se limpia al cerrar). La plantilla ya convierte un null del
   * buscador en "", así que aquí siempre llega texto.
   */
  filtraPersonas(e: EdicionArea, fila: FilaPersona, texto: string): void {
    if (texto === fila.filtro) {
      return;
    }
    fila.filtro = texto;
    this.filtraFila(e, fila);
  }

  /**
   * Las opciones de un renglón: todas sin filtro (el mismo arreglo), o las
   * que tienen cada palabra escrita en cualquier parte del nombre, sin
   * acentos ni mayúsculas. La persona ya elegida se queda aunque no
   * coincida, para que el select no pierda lo que muestra, pero al final:
   * la primera opción es la primera coincidencia y el buscador la deja
   * activa (Enter elige a quien se buscó, no a quien ya estaba).
   */
  private filtraFila(e: EdicionArea, fila: FilaPersona): void {
    const palabras = normalizaBusqueda(fila.filtro).split(/\s+/).filter((p) => p.length > 0);
    if (palabras.length === 0) {
      fila.opciones = e.personas;
      return;
    }
    const elegida = fila.usuario.idUsuario;
    const coinciden = e.personas.filter((p) => palabras.every((palabra) => p.clave.includes(palabra)));
    const propia =
      elegida != null && !coinciden.some((p) => p.idUsuario === elegida)
        ? e.personas.find((p) => p.idUsuario === elegida)
        : undefined;
    fila.opciones = propia ? [...coinciden, propia] : coinciden;
  }

  private marcaOcupadas(e: EdicionArea): void {
    e.ocupadas = new Set(
      e.filas.map((f) => f.usuario.idUsuario).filter((id): id is number => id != null),
    );
  }

  /**
   * Las adm_area, más la ligada aunque no venga en el catálogo; y los
   * elegibles, más quienes ya estaban en el área aunque ya no lo sean.
   * Se arman aquí, una vez, para que los selectores reciban siempre los
   * mismos objetos (ver EdicionArea).
   */
  private armaOpciones(e: EdicionArea): EdicionArea {
    const area = e.area;
    e.opcionesAreaCos =
      area.idArea == null || this.areasCos.some((a) => a.id === area.idArea)
        ? this.areasCos
        : [{ id: area.idArea, area: area.nombreArea || `Área ${area.idArea}` }, ...this.areasCos];
    const fuera = aOpcionesPersona(
      e.filas
        .map((f) => f.usuario)
        .filter((u) => u.idUsuario != null && !this.usuariosElegibles.some((el) => el.idUsuario === u.idUsuario)),
    );
    e.personas = fuera.length > 0 ? [...fuera, ...this.personasElegibles] : this.personasElegibles;
    // Un área que dejó de venir en la consulta ya no tiene renglón donde pintarse.
    e.arriba = area.idBscArea == null || !this.areas.some((a) => a.idBscArea === area.idBscArea);
    e.filas.forEach((f) => this.filtraFila(e, f));
    this.marcaOcupadas(e);
    return e;
  }

  /** El nombre para mostrar de un renglón del editor: el elegible o el que ya traía. */
  nombreUsuario(u: BscAreaUsuario): string | undefined {
    if (u.idUsuario == null) {
      return undefined;
    }
    return this.usuariosElegibles.find((e) => e.idUsuario === u.idUsuario)?.usuario || u.usuario || undefined;
  }

  get sinResponsable(): boolean {
    return !!this.edicion && !this.edicion.filas.some((f) => f.usuario.rol === "RESPONSABLE");
  }

  guardarArea(): void {
    const e = this.edicion;
    if (!e || this.guardandoArea) {
      return;
    }
    const nombre = (e.area.nombre ?? "").trim();
    const errores: string[] = [];
    if (!nombre) {
      errores.push("El área necesita el nombre con que aparece en el FOR-011.");
    }
    const filas = e.filas.map((f) => f.usuario);
    if (filas.some((u) => u.idUsuario == null || !u.rol)) {
      errores.push("Cada usuario del área necesita persona y rol.");
    }
    const ids = filas.map((u) => u.idUsuario).filter((id) => id != null);
    if (new Set(ids).size !== ids.length) {
      errores.push("Una persona aparece dos veces en el área.");
    }
    if (errores.length > 0) {
      this.msg.warningCall(errores.join(" "), "Revisa el área", null);
      return;
    }
    // Alias sin espacios sobrantes ni separadores vacíos.
    const alias = (e.area.aliasExcel ?? "")
      .split("|")
      .map((a) => a.trim())
      .filter((a) => a.length > 0)
      .join("|");
    const area: BscArea = {
      idBscArea: e.area.idBscArea,
      nombre,
      aliasExcel: alias || null,
      idArea: e.area.idArea ?? null,
      viaCaptura: e.area.viaCaptura ?? "AMBAS",
      orden: e.area.orden ?? 0,
      estatus: e.activa ? "ACTIVO" : "INACTIVO",
    };
    const usuarios: BscAreaUsuario[] = filas.map((u) => ({
      id: u.id,
      idBscArea: e.area.idBscArea,
      idUsuario: u.idUsuario,
      // El nombre va para el aviso "no es empleado activo" del servidor.
      usuario: this.nombreUsuario(u),
      rol: u.rol,
      vigente: true,
    }));

    this.guardandoArea = true;
    this.cdr.detectChanges();
    this.bscService.parametrosAreaGuardar(area, usuarios).subscribe({
      next: (res) => {
        this.guardandoArea = false;
        if (res?.codigoError) {
          this.msg.warningCall(this.mensaje(res, "No se pudo guardar el área."), null, null);
          this.cdr.detectChanges();
          return;
        }
        // Guardar un área no guarda el año: si hay parámetros o fechas
        // editados sin guardar, la recarga los conserva.
        const anioSinGuardar = !!this.ciclo && !this.cerrado && this.huellaActual() !== this.huella;
        // La recarga quita la tabla mientras consulta: el foco vuelve al
        // botón del área (o a «Agregar área») cuando se pinta de nuevo.
        // Y no antes de que se cierre el aviso: detrás del modal, Enter
        // («Aceptar») activaría el lápiz y reabriría el editor.
        this.focoPendiente = e.area.idBscArea == null ? ID_AGREGAR_AREA : this.focoEditor;
        this.edicion = null;
        this.focoEditor = null;
        this.avisoAbierto = true;
        this.avisaGuardado(res, `El área ${nombre} quedó guardada.`, () => {
          this.avisoAbierto = false;
          this.reponeFoco();
        });
        this.consultar(anioSinGuardar);
      },
      error: () => {
        this.guardandoArea = false;
        this.msg.errorCall("No se pudo guardar el área: sin conexión con el servidor.", null, null);
        this.cdr.detectChanges();
      },
    });
  }

  /* ============================ Presentación ============================ */

  nombreAreaCos(area: BscArea): string {
    if (area.nombreArea) {
      return area.nombreArea;
    }
    if (area.idArea == null) {
      return "Sin ligar";
    }
    return this.areasCos.find((a) => a.id === area.idArea)?.area ?? `Área ${area.idArea}`;
  }

  textoVia(via: ViaCapturaBsc | undefined): string {
    return VIAS.find((v) => v.valor === (via ?? "AMBAS"))?.texto ?? "Ambas";
  }

  textoRol(rol: RolAreaBsc | undefined): string {
    return ROLES.find((r) => r.valor === rol)?.texto ?? "";
  }

  aliasDe(area: BscArea): string[] {
    return (area.aliasExcel ?? "")
      .split("|")
      .map((a) => a.trim())
      .filter((a) => a.length > 0);
  }

  usuariosDe(area: BscArea): BscAreaUsuario[] {
    return (area.usuarios ?? []).filter((u) => u.vigente !== false);
  }

  trackArea(_: number, area: BscArea): number | string {
    return area.idBscArea ?? area.nombre ?? "";
  }

  trackAreaCos(_: number, a: BscAreaCos): number | string {
    return a.id ?? a.area ?? "";
  }

  trackValor(_: number, op: { valor: string }): string {
    return op.valor;
  }

  trackFila(_: number, fila: FilaPersona): FilaPersona {
    return fila;
  }

  trackPersona(_: number, op: OpcionPersona): number {
    return op.idUsuario;
  }

  /* ============================ Foco ============================ */

  /** Lo llaman la consulta y el cierre del aviso: repone el foco la última de las dos en terminar. */
  private reponeFoco(): void {
    if (this.avisoAbierto || this.loading) {
      return;
    }
    const foco = this.focoPendiente;
    this.focoPendiente = null;
    this.enfoca(foco);
  }

  private enfoca(id: string | null): void {
    if (id) {
      this.host.nativeElement.querySelector<HTMLElement>(`#${id}`)?.focus();
    }
  }

  /* ============================ Utilerías ============================ */

  /** Éxito, o aviso si el servidor guardó pero tiene algo que decir (usuario sin empleado). */
  private avisaGuardado(res: BscRes, texto: string, alCerrar: (() => void) | null = null): void {
    const avisos = res?.avisos ?? [];
    if (avisos.length > 0) {
      this.msg.warningCall(`${texto} ${avisos.join(" ")}`, "Guardado con avisos", alCerrar);
    } else {
      this.msg.successCall(texto, null, alCerrar);
    }
  }

  private mensaje(res: BscRes | null | undefined, porOmision: string): string {
    // AUTH-001 viaja en un 200 con codigoError: ningún interceptor lo ve.
    if (res?.codigoError === "AUTH-001") {
      return "Tu sesión caducó. Vuelve a entrar.";
    }
    return res?.mensajeError || porOmision;
  }

  /** Fracción 0..1 → porcentaje, sin el ruido del punto flotante (0.95 → 95). */
  private aPct(fraccion: number | null | undefined): number | null {
    if (fraccion == null) {
      return null;
    }
    return Math.round(Number(fraccion) * 100 * 10000) / 10000;
  }

  /** Porcentaje → fracción con 6 decimales (95 → 0.95), como viaja en la API. */
  private aFraccion(porcentaje: number | null | undefined): number | undefined {
    if (porcentaje == null || (porcentaje as unknown) === "") {
      return undefined;
    }
    return Number((Number(porcentaje) / 100).toFixed(6));
  }

  private formaVacia(): FormaCiclo {
    return {
      verde: null,
      amarillo: null,
      objetivo: null,
      diaLimite: null,
      anticipacion: null,
      excluirKpiNa: true,
      exigirSumaPesos: true,
      toleranciaPts: null,
      exigirMeses: true,
      version: "",
    };
  }
}

/** Ids del editor de áreas, para llevar el foco y la vista (ver abreEditor). */
const ID_EDITOR = "bsc-area-editor";
const ID_NOMBRE = "bsc-area-nombre";
const ID_AGREGAR_AREA = "bsc-area-agregar";
const ID_AGREGAR_USUARIO = "bsc-area-agregar-usuario";

/** El select «Persona N» del renglón (índice desde 0). */
function idPersona(indice: number): string {
  return `bsc-area-persona-${indice}`;
}

/** El botón «Quitar» del renglón (índice desde 0). */
function idQuitar(indice: number): string {
  return `bsc-area-quitar-${indice}`;
}

function idEditarArea(idBscArea: number): string {
  return `bsc-area-editar-${idBscArea}`;
}

/** Sin acentos ni mayúsculas: «López» y «lopez» son lo mismo para el buscador de personas. */
function normalizaBusqueda(texto: string | null | undefined): string {
  return (texto ?? "").normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().trim();
}

/** Usuarios (con id) como opciones del buscador de personas. */
function aOpcionesPersona(usuarios: BscAreaUsuario[]): OpcionPersona[] {
  return usuarios
    .filter((u) => u.idUsuario != null)
    .map((u) => {
      const usuario = u.usuario || `Usuario ${u.idUsuario}`;
      return { idUsuario: u.idUsuario as number, usuario, clave: normalizaBusqueda(usuario) };
    });
}

/** Lo que dijo el recálculo: cuántos trimestres revisó y cuántos cambiaron. */
function textoRecalculo(anio: number, revisados: number, cambiados: number): string {
  if (revisados === 0) {
    return `${anio} no tenía snapshots que recalcular.`;
  }
  const trimestres = revisados === 1 ? "trimestre" : "trimestres";
  let cambio = "ninguno cambió.";
  if (cambiados !== 0) {
    cambio = `${cambiados} ${cambiados === 1 ? "cambió" : "cambiaron"}.`;
  }
  return `Se recalcularon ${revisados} ${trimestres} de ${anio}: ${cambio}`;
}
